import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AppShell, Box, Transition, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Header } from './Header';
import { Navbar, NavbarState } from './Navbar';
import { GlobalJobMonitor } from '@/features/story/components/GlobalJobMonitor';

const NAVBAR_STATE_KEY = 'reading-app-navbar-state';

export function AppLayout() {
  const location = useLocation();
  const isLargeScreen = useMediaQuery('(min-width: 1024px)');
  const theme = useMantineTheme();

  // Load navbar state from localStorage or default based on screen size
  const [navbarState, setNavbarState] = useState<NavbarState>(() => {
    const saved = localStorage.getItem(NAVBAR_STATE_KEY);
    if (saved && ['expanded', 'collapsed'].includes(saved)) {
      return saved as NavbarState;
    }
    // Default: expanded on large screens, collapsed on small screens
    return isLargeScreen ? 'expanded' : 'collapsed';
  });

  // Persist navbar state to localStorage
  useEffect(() => {
    localStorage.setItem(NAVBAR_STATE_KEY, navbarState);
  }, [navbarState]);

  // Get navbar width based on state
  const getNavbarWidth = () => {
    return navbarState === 'expanded' ? 220 : 70;
  };

  return (
    <>
      <GlobalJobMonitor />
      <AppShell
        header={{ height: 60 }}
        navbar={{
          width: getNavbarWidth(),
          breakpoint: 'sm',
        }}
        padding="md"
        styles={{
          main: {
            backgroundColor: theme.other.mainBg,
          },
          header: {
            backgroundColor: theme.other.headerBg,
            borderBottom: '1px solid #e9ecef',
          },
          navbar: {
            border: 'none',
          },
        }}
      >
        <AppShell.Header>
          <Header />
        </AppShell.Header>

        <AppShell.Navbar p={0}>
          <Navbar state={navbarState} onStateChange={setNavbarState} />
        </AppShell.Navbar>

        <AppShell.Main>
          <Transition
            key={location.pathname}
            mounted
            transition="fade"
            duration={200}
            timingFunction="ease"
          >
            {(transitionStyles) => (
              <Box mih="100%" style={transitionStyles}>
                <Outlet />
              </Box>
            )}
          </Transition>
        </AppShell.Main>
      </AppShell>
    </>
  );
}
